import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchUsers, createUser, updateUser } from '../store/slices/userSlice';
import { createChatRoom, toggleChat } from '../store/slices/chatSlice';
import { refreshUserData } from '../store/slices/authSlice';
import { toast } from 'react-toastify';
import { PlusIcon, PencilIcon, CheckIcon, XMarkIcon, MagnifyingGlassIcon, ChatBubbleLeftRightIcon } from '@heroicons/react/24/outline';
import Select from 'react-select';
import { INSTITUTION_TYPES } from '../utils/constants';
import { COUNTRIES, REGIONS } from '../utils/countries';
import Pagination from '../components/Pagination';

const ROLE_OPTIONS = [
  { value: 'superadmin', label: 'Super Admin' },
  { value: 'global_admin_hq', label: 'Global Admin (HQ)' },
  { value: 'country_director', label: 'Country Director' },
  { value: 'institutional_relations_officer', label: 'Institutional Relations Officer' },
  { value: 'legal_compliance_team', label: 'Legal & Compliance Team' },
  { value: 'read_only_auditor', label: 'Read-only Auditor' }
];

const Users = () => {
  const dispatch = useDispatch();
  const { items = [], loading, pagination } = useSelector((state) => state.users);
  const { user: currentUser } = useSelector((state) => state.auth);

  // Check if current user can manage users - only superadmin
  const canManageUsers = currentUser?.role === 'superadmin';

  // Filters and pagination
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);

  // Helper function to get role display info
  const getRoleInfo = (roleValue) => {
    const role = ROLE_OPTIONS.find(r => r.value === roleValue);
    return role || { value: roleValue, label: roleValue };
  };

  // Helper function to get role badge color
  const getRoleBadgeColor = (roleValue) => {
    const colors = {
      'superadmin': 'bg-purple-100 text-purple-800',
      'global_admin_hq': 'bg-blue-100 text-blue-800',
      'country_director': 'bg-green-100 text-green-800',
      'institutional_relations_officer': 'bg-yellow-100 text-yellow-800',
      'legal_compliance_team': 'bg-red-100 text-red-800',
      'read_only_auditor': 'bg-gray-100 text-gray-800'
    };
    return colors[roleValue] || 'bg-gray-100 text-gray-800';
  };
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [selectedCountries, setSelectedCountries] = useState([]);
  const [selectedRegions, setSelectedRegions] = useState([]);
  const [selectedInstitutionTypes, setSelectedInstitutionTypes] = useState([]);
  const [showPasswordField, setShowPasswordField] = useState(true);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'read_only_auditor',
    isActive: true,
    documentLinks: [],
    canExportExcel: false,
    canComposeEmail: false,
    canAccessMobileView: false,
    canDeleteContacts: false,
    sendEmailIndividually: false,
    sendingEmail: null,
    emailAppPassword: '',
    assignedTemplateIds: [],
    assignedFooterIds: [],
    defaultTemplateId: null,
    defaultFooterId: null
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [availableTemplates, setAvailableTemplates] = useState([]);
  const [availableFooters, setAvailableFooters] = useState([]);

  useEffect(() => {
    const filters = {
      page: currentPage,
      limit: itemsPerPage
    };

    if (searchTerm) filters.search = searchTerm;
    if (roleFilter) filters.role = roleFilter;
    if (statusFilter) filters.isActive = statusFilter;

    console.log('Fetching users with filters:', filters);
    dispatch(fetchUsers(filters));
  }, [dispatch, currentPage, itemsPerPage, searchTerm, roleFilter, statusFilter]);

  // Fetch templates and footers for assignment (Super Admin only)
  useEffect(() => {
    if (canManageUsers) {
      fetchTemplatesAndFooters();
    }
  }, [canManageUsers]);

  const fetchTemplatesAndFooters = async () => {
    try {
      const api = require('../utils/api').default;
      const [templatesRes, footersRes] = await Promise.all([
        api.get('/templates'),
        api.get('/footers')
      ]);
      setAvailableTemplates(templatesRes.data || []);
      setAvailableFooters(footersRes.data?.footers || footersRes.data || []);
    } catch (error) {
      console.error('Error fetching templates/footers:', error);
    }
  };

  const handleInstitutionTypeToggle = (institutionType) => {
    setSelectedInstitutionTypes(prev =>
      prev.includes(institutionType)
        ? prev.filter(it => it !== institutionType)
        : [...prev, institutionType]
    );
  };

  const handleEdit = async (user) => {
    setEditingUser(user);
    setFormData({
      name: user.name || '',
      email: user.email || '',
      password: '',
      role: user.role || 'read_only_auditor',
      isActive: user.isActive !== undefined ? user.isActive : true,
      documentLinks: user.documentLinks || [],
      canExportExcel: user.canExportExcel || false,
      canComposeEmail: user.canComposeEmail || false,
      canAccessMobileView: user.canAccessMobileView || false,
      canDeleteContacts: user.canDeleteContacts || false,
      sendEmailIndividually: user.sendEmailIndividually || false,
      sendingEmail: user.sendingEmail || null,
      emailAppPassword: '', // Never pre-fill password for security
      assignedTemplateIds: [],
      assignedFooterIds: [],
      defaultTemplateId: user.defaultTemplateId || null,
      defaultFooterId: user.defaultFooterId || null
    });

    // Fetch assigned templates and footers for this user
    if (user.sendEmailIndividually) {
      try {
        const api = require('../utils/api').default;
        const [templatesRes, footersRes] = await Promise.all([
          api.get('/templates'),
          api.get('/footers')
        ]);
        
        const templates = templatesRes.data || [];
        const footers = footersRes.data?.footers || footersRes.data || [];
        
        // Find templates assigned to this user
        // Check both camelCase (ORM) and snake_case (raw SQL) column names
        const assignedTemplates = templates
          .filter(t => {
            const ids = t.allowedUserIds || t.allowed_user_ids;
            return ids && ids.includes(user.id);
          })
          .map(t => t.id);
        
        // Find footers assigned to this user
        // Check both camelCase (ORM) and snake_case (raw SQL) column names
        const assignedFooters = footers
          .filter(f => {
            const ids = f.allowedUserIds || f.allowed_user_ids;
            return ids && ids.includes(user.id);
          })
          .map(f => f.id);
        
        setFormData(prev => ({
          ...prev,
          assignedTemplateIds: assignedTemplates,
          assignedFooterIds: assignedFooters
        }));
      } catch (error) {
        console.error('Error fetching user assignments:', error);
      }
    }

    // Set countries
    if (user.countries && user.countries.length > 0) {
      setSelectedCountries(user.countries.map(c => ({ value: c, label: c })));
    } else {
      setSelectedCountries([]);
    }

    // Set regions
    if (user.regions && user.regions.length > 0) {
      setSelectedRegions(user.regions.map(r => ({ value: r, label: r })));
    } else {
      setSelectedRegions([]);
    }

    // Set institution types
    if (user.institutionTypes && user.institutionTypes.length > 0) {
      setSelectedInstitutionTypes(user.institutionTypes);
    } else {
      setSelectedInstitutionTypes([]);
    }
    setShowPasswordField(false);
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isSubmitting) return;

    // Validate individual email sending fields
    if (formData.sendEmailIndividually) {
      if (!formData.sendingEmail || formData.sendingEmail.trim() === '') {
        toast.error('Sending email address is required when individual email sending is enabled');
        setIsSubmitting(false);
        return;
      }
      
      // Validate email format
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.sendingEmail)) {
        toast.error('Please enter a valid sending email address');
        setIsSubmitting(false);
        return;
      }
      
      // For new users or when updating without existing password
      if (!editingUser || !editingUser.hasEmailAppPassword) {
        if (!formData.emailAppPassword || formData.emailAppPassword.trim() === '') {
          toast.error('Email app password is required when individual email sending is enabled');
          setIsSubmitting(false);
          return;
        }
      }
    }

    setIsSubmitting(true);
    try {
      const countries = selectedCountries.map(c => c.value);
      const regions = selectedRegions.map(r => r.value);

      const userData = {
        name: formData.name,
        email: formData.email,
        role: formData.role,
        isActive: formData.isActive,
        countries,
        regions,
        institutionTypes: selectedInstitutionTypes,
        documentLinks: formData.documentLinks,
        canExportExcel: formData.canExportExcel,
        canComposeEmail: formData.canComposeEmail,
        canAccessMobileView: formData.canAccessMobileView,
        canDeleteContacts: formData.canDeleteContacts,
        sendEmailIndividually: formData.sendEmailIndividually,
        sendingEmail: formData.sendEmailIndividually ? (formData.sendingEmail || null) : null,
        defaultTemplateId: formData.defaultTemplateId,
        defaultFooterId: formData.defaultFooterId
      };

      // Only include password if it's provided (for updates) or if creating new user
      if (editingUser) {
        if (formData.password && formData.password.trim() !== '') {
          userData.password = formData.password;
        }
        // Only include emailAppPassword if it's been changed (not empty)
        if (formData.emailAppPassword && formData.emailAppPassword.trim() !== '') {
          userData.emailAppPassword = formData.emailAppPassword;
        }
        // If disabling individual sending, explicitly clear the credentials
        if (!formData.sendEmailIndividually) {
          userData.emailAppPassword = null;
        }
        await dispatch(updateUser({ id: editingUser.id, data: userData })).unwrap();
        toast.success('User updated successfully');

        // If the updated user is the current user, refresh their data
        if (currentUser && currentUser.id === editingUser.id) {
          dispatch(refreshUserData());
        }
      } else {
        userData.password = formData.password;
        // Only include emailAppPassword if individual sending is enabled
        userData.emailAppPassword = formData.sendEmailIndividually ? formData.emailAppPassword : null;
        const result = await dispatch(createUser(userData)).unwrap();
        toast.success('User created successfully');
        
        // Assign templates and footers if individual sending is enabled
        if (formData.sendEmailIndividually && result.id) {
          await assignTemplatesAndFooters(result.id);
        }
      }

      // For updates, assign templates and footers if individual sending is enabled
      // Only reassign if the templates/footers lists actually loaded (to avoid wiping assignments)
      if (editingUser && formData.sendEmailIndividually && (availableTemplates.length > 0 || availableFooters.length > 0)) {
        await assignTemplatesAndFooters(editingUser.id);
      }

      setShowModal(false);
      resetForm();
      // Refresh the list
      dispatch(fetchUsers({ page: currentPage, limit: itemsPerPage, search: searchTerm, role: roleFilter, isActive: statusFilter }));
    } catch (err) {
      toast.error(err.message || (editingUser ? 'Failed to update user' : 'Failed to create user'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const assignTemplatesAndFooters = async (userId) => {
    try {
      const api = require('../utils/api').default;
      await Promise.all([
        api.post('/templates/assign-user', {
          userId,
          templateIds: formData.assignedTemplateIds
        }),
        api.post('/footers/assign-user', {
          userId,
          footerIds: formData.assignedFooterIds
        })
      ]);
    } catch (error) {
      console.error('Error assigning templates/footers:', error);
      toast.warning('User saved but template/footer assignment may have failed');
    }
  };

  const resetForm = () => {
    setEditingUser(null);
    setFormData({
      name: '',
      email: '',
      password: '',
      role: 'read_only_auditor',
      isActive: true,
      documentLinks: [],
      canExportExcel: false,
      canComposeEmail: false,
      canAccessMobileView: false,
      canDeleteContacts: false,
      sendEmailIndividually: false,
      sendingEmail: null,
      emailAppPassword: '',
      assignedTemplateIds: [],
      assignedFooterIds: [],
      defaultTemplateId: null,
      defaultFooterId: null
    });
    setSelectedCountries([]);
    setSelectedRegions([]);
    setSelectedInstitutionTypes([]);
    setShowPasswordField(true);
  };

  const handleToggleActive = async (user) => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    try {
      await dispatch(updateUser({
        id: user.id,
        data: { ...user, isActive: !user.isActive }
      })).unwrap();
      toast.success(`User ${!user.isActive ? 'activated' : 'deactivated'} successfully`);
      // Refresh the list
      dispatch(fetchUsers({ page: currentPage, limit: itemsPerPage, search: searchTerm, role: roleFilter, isActive: statusFilter }));
    } catch (err) {
      toast.error('Failed to update user status');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAddDocumentLink = () => {
    setFormData({
      ...formData,
      documentLinks: [...formData.documentLinks, { label: '', url: '' }]
    });
  };

  const handleRemoveDocumentLink = (index) => {
    const newLinks = formData.documentLinks.filter((_, i) => i !== index);
    setFormData({ ...formData, documentLinks: newLinks });
  };

  const handleDocumentLinkChange = (index, field, value) => {
    const newLinks = [...formData.documentLinks];
    newLinks[index][field] = value;
    setFormData({ ...formData, documentLinks: newLinks });
  };

  const handleMessage = async (targetUser) => {
    try {
      if (currentUser.id === targetUser.id) {
        toast.info("You cannot chat with yourself");
        return;
      }
      await dispatch(createChatRoom(targetUser.id)).unwrap();
      if (!document.querySelector('.chat-overlay-open')) { // simplistic check or just toggle
        // Actually, createChatRoom sets isChatOpen=true in reducer, so just need to ensure overlay handles it.
        // But let's dispatch toggleChat just in case or rely on the slice.
        // My slice: .addCase(createChatRoom.fulfilled, (state) => { state.isChatOpen = true; ... })
        // So no need to toggle manually if successful.
      }
    } catch (error) {
      toast.error('Failed to start chat');
    }
  };



  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl sm:text-4xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">Users Management</h1>
          <p className="mt-2 text-base text-gray-600 font-medium">Manage admin users and their privileges</p>
        </div>
        {canManageUsers && (
          <button
            onClick={() => setShowModal(true)}
            className="flex items-center px-5 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl hover:from-blue-700 hover:to-indigo-700 transition-all duration-200 shadow-lg hover:shadow-xl hover:scale-105 font-semibold"
          >
            <PlusIcon className="h-5 w-5 mr-2" />
            Add User
          </button>
        )}
      </div>

      {/* Search and Filters */}
      <div className="bg-white shadow-lg rounded-xl p-6 border border-gray-200">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Search */}
          <div className="relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search by name or email..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* Role Filter */}
          <div>
            <select
              value={roleFilter}
              onChange={(e) => {
                setRoleFilter(e.target.value);
                setCurrentPage(1);
              }}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">All Roles</option>
              {ROLE_OPTIONS.map(role => (
                <option key={role.value} value={role.value}>{role.label}</option>
              ))}
            </select>
          </div>

          {/* Status Filter */}
          <div>
            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setCurrentPage(1);
              }}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">All Status</option>
              <option value="true">Active</option>
              <option value="false">Inactive</option>
            </select>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      ) : (
        <div className="bg-white shadow-xl rounded-2xl overflow-hidden border border-gray-200">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gradient-to-r from-gray-50 to-gray-100">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Name</th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Email</th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Role</th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Institution Types</th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Countries</th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Document Links</th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Excel</th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Email</th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Mobile</th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Status</th>
                  {canManageUsers && <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider sticky right-0 bg-gradient-to-r from-gray-50 to-gray-100 z-20">Actions</th>}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {items.length > 0 ? items.map((user) => (
                  <tr key={user.id} className="hover:bg-blue-50 transition-all duration-200 hover:shadow-sm">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900">{user.name}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{user.email}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-3 py-1 inline-flex text-xs leading-5 font-bold rounded-full ${getRoleBadgeColor(user.role)}`}>
                        {getRoleInfo(user.role).label}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {user.institutionTypes && user.institutionTypes.length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {user.institutionTypes.slice(0, 2).map((type, idx) => (
                            <span key={idx} className="px-2 py-1 bg-green-100 text-green-700 rounded-lg text-xs font-medium">
                              {type}
                            </span>
                          ))}
                          {user.institutionTypes.length > 2 && (
                            <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded-lg text-xs font-medium">
                              +{user.institutionTypes.length - 2} more
                            </span>
                          )}
                        </div>
                      ) : (
                        <span className="text-gray-400 font-medium">None</span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {user.countries && user.countries.length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {user.countries.map((country, idx) => (
                            <span key={idx} className="px-2 py-1 bg-blue-100 text-blue-700 rounded-lg text-xs font-medium">
                              {country}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span className="text-gray-400 font-medium">All</span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {user.documentLinks && user.documentLinks.length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {user.documentLinks.slice(0, 2).map((link, idx) => (
                            <a
                              key={idx}
                              href={link.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="px-2 py-1 bg-purple-100 text-purple-700 rounded-lg text-xs font-medium hover:bg-purple-200 transition-colors"
                              title={link.label}
                            >
                              {link.label || 'Link'}
                            </a>
                          ))}
                          {user.documentLinks.length > 2 && (
                            <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded-lg text-xs font-medium">
                              +{user.documentLinks.length - 2} more
                            </span>
                          )}
                        </div>
                      ) : (
                        <span className="text-gray-400 font-medium">None</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {user.canExportExcel ? (
                        <span className="flex items-center text-green-700 bg-green-100 px-3 py-1 rounded-full w-fit">
                          <CheckIcon className="h-4 w-4 mr-1" />
                          <span className="text-xs font-bold">Enabled</span>
                        </span>
                      ) : (
                        <span className="flex items-center text-gray-500 bg-gray-100 px-3 py-1 rounded-full w-fit">
                          <XMarkIcon className="h-4 w-4 mr-1" />
                          <span className="text-xs font-bold">Disabled</span>
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {user.canComposeEmail ? (
                        <span className="flex items-center text-blue-700 bg-blue-100 px-3 py-1 rounded-full w-fit">
                          <CheckIcon className="h-4 w-4 mr-1" />
                          <span className="text-xs font-bold">Enabled</span>
                        </span>
                      ) : (
                        <span className="flex items-center text-gray-500 bg-gray-100 px-3 py-1 rounded-full w-fit">
                          <XMarkIcon className="h-4 w-4 mr-1" />
                          <span className="text-xs font-bold">Disabled</span>
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {user.canAccessMobileView ? (
                        <span className="flex items-center text-purple-700 bg-purple-100 px-3 py-1 rounded-full w-fit">
                          <CheckIcon className="h-4 w-4 mr-1" />
                          <span className="text-xs font-bold">Enabled</span>
                        </span>
                      ) : (
                        <span className="flex items-center text-gray-500 bg-gray-100 px-3 py-1 rounded-full w-fit">
                          <XMarkIcon className="h-4 w-4 mr-1" />
                          <span className="text-xs font-bold">Disabled</span>
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {user.isActive ? (
                        <span className="flex items-center text-green-700 bg-green-100 px-3 py-1 rounded-full w-fit">
                          <CheckIcon className="h-4 w-4 mr-1" />
                          <span className="text-xs font-bold">Active</span>
                        </span>
                      ) : (
                        <span className="flex items-center text-red-700 bg-red-100 px-3 py-1 rounded-full w-fit">
                          <XMarkIcon className="h-4 w-4 mr-1" />
                          <span className="text-xs font-bold">Inactive</span>
                        </span>
                      )}
                    </td>
                    {canManageUsers && (
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium sticky right-0 bg-white z-30 shadow-[-4px_0_8px_rgba(0,0,0,0.05)]">
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleEdit(user)}
                            className="text-blue-600 hover:text-blue-800 hover:bg-blue-50 p-2.5 rounded-lg transition-all duration-200 min-w-[44px] min-h-[44px] flex items-center justify-center"
                            title="Edit User"
                            aria-label="Edit User"
                          >
                            <PencilIcon className="h-5 w-5" />
                          </button>
                          <button
                            onClick={() => handleToggleActive(user)}
                            className={`${user.isActive ? 'text-orange-600 hover:text-orange-800 hover:bg-orange-50' : 'text-green-600 hover:text-green-800 hover:bg-green-50'} p-2.5 rounded-lg transition-all duration-200 min-w-[44px] min-h-[44px] flex items-center justify-center`}
                            title={user.isActive ? 'Deactivate' : 'Activate'}
                            aria-label={user.isActive ? 'Deactivate User' : 'Activate User'}
                          >
                            {user.isActive ? <XMarkIcon className="h-5 w-5" /> : <CheckIcon className="h-5 w-5" />}
                          </button>
                          <button
                            onClick={() => handleMessage(user)}
                            className="text-indigo-600 hover:text-indigo-800 hover:bg-indigo-50 p-2.5 rounded-lg transition-all duration-200 min-w-[44px] min-h-[44px] flex items-center justify-center"
                            title="Message User"
                            aria-label="Message User"
                          >
                            <ChatBubbleLeftRightIcon className="h-5 w-5" />
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                )) : (
                  <tr>
                    <td colSpan="9" className="px-6 py-12 text-center text-gray-500">
                      <div className="flex flex-col items-center justify-center">
                        <p className="text-lg font-medium">No users found</p>
                        <p className="text-sm mt-1">Try adjusting your search or filters</p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {pagination && pagination.total > 0 && (
            <Pagination
              currentPage={currentPage}
              totalPages={pagination.totalPages}
              totalItems={pagination.total}
              itemsPerPage={itemsPerPage}
              onPageChange={(page) => setCurrentPage(page)}
              onItemsPerPageChange={(limit) => {
                setItemsPerPage(limit);
                setCurrentPage(1);
              }}
            />
          )}
        </div>
      )}

      {/* Create User Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity bg-gray-900 bg-opacity-75" onClick={() => setShowModal(false)}></div>

            <div className="inline-block align-bottom bg-white rounded-2xl text-left overflow-hidden shadow-2xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
              <form onSubmit={handleSubmit}>
                <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-5">
                  <h3 className="text-2xl font-bold text-white">
                    {editingUser ? 'Edit User' : 'Add New User'}
                  </h3>
                </div>

                <div className="bg-white px-6 py-6 max-h-[70vh] overflow-y-auto">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Name *</label>
                      <input
                        type="text"
                        required
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Full name"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Email *</label>
                      <input
                        type="email"
                        required
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="email@example.com"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Password {editingUser ? '(leave blank to keep current)' : '*'}
                      </label>
                      {editingUser && !showPasswordField ? (
                        <button
                          type="button"
                          onClick={() => setShowPasswordField(true)}
                          className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                        >
                          + Change Password
                        </button>
                      ) : (
                        <div>
                          <input
                            type="password"
                            required={!editingUser}
                            value={formData.password}
                            onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            placeholder="Minimum 6 characters"
                            minLength="6"
                          />
                          {editingUser && (
                            <button
                              type="button"
                              onClick={() => {
                                setShowPasswordField(false);
                                setFormData({ ...formData, password: '' });
                              }}
                              className="text-sm text-gray-600 hover:text-gray-700 mt-1"
                            >
                              Cancel password change
                            </button>
                          )}
                        </div>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Role *</label>
                      <select
                        required
                        value={formData.role}
                        onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        {ROLE_OPTIONS.map(role => (
                          <option key={role.value} value={role.value}>{role.label}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                      <div className="flex items-center space-x-4">
                        <label className="flex items-center cursor-pointer">
                          <input
                            type="radio"
                            checked={formData.isActive === true}
                            onChange={() => setFormData({ ...formData, isActive: true })}
                            className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                          />
                          <span className="ml-2 text-sm text-gray-700">Active</span>
                        </label>
                        <label className="flex items-center cursor-pointer">
                          <input
                            type="radio"
                            checked={formData.isActive === false}
                            onChange={() => setFormData({ ...formData, isActive: false })}
                            className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                          />
                          <span className="ml-2 text-sm text-gray-700">Inactive</span>
                        </label>
                      </div>
                    </div>

                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-2">Assigned Countries</label>
                      <p className="text-xs text-gray-500 mb-2">Leave empty for access to all countries</p>
                      <Select
                        isMulti
                        options={COUNTRIES}
                        value={selectedCountries}
                        onChange={setSelectedCountries}
                        placeholder="Select countries..."
                        className="react-select-container"
                        classNamePrefix="react-select"
                      />
                    </div>



                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-3">Institution Types</label>
                      <p className="text-xs text-gray-500 mb-3">Select which institution types this user can access within their categories.</p>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-48 overflow-y-auto border border-gray-200 rounded-lg p-3">
                        {INSTITUTION_TYPES.map((institutionType) => (
                          <label key={institutionType} className="flex items-center space-x-2 cursor-pointer p-2 hover:bg-gray-50 rounded">
                            <input
                              type="checkbox"
                              checked={selectedInstitutionTypes.includes(institutionType)}
                              onChange={() => handleInstitutionTypeToggle(institutionType)}
                              disabled={formData.role === 'superadmin'}
                              className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 disabled:opacity-50"
                            />
                            <span className="text-sm text-gray-700">{institutionType}</span>
                          </label>
                        ))}
                      </div>
                      {formData.role === 'superadmin' && (
                        <p className="text-xs text-blue-600 mt-2">Superadmin automatically has access to all institution types</p>
                      )}
                    </div>

                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-2">Document Links</label>
                      <p className="text-xs text-gray-500 mb-3">Add links to Google Drive, documents, or other resources</p>
                      <div className="space-y-3">
                        {formData.documentLinks.map((link, index) => (
                          <div key={index} className="flex gap-2 items-start">
                            <div className="flex-1 grid grid-cols-2 gap-2">
                              <input
                                type="text"
                                value={link.label}
                                onChange={(e) => handleDocumentLinkChange(index, 'label', e.target.value)}
                                placeholder="Label (e.g., Project Files)"
                                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                              />
                              <input
                                type="url"
                                value={link.url}
                                onChange={(e) => handleDocumentLinkChange(index, 'url', e.target.value)}
                                placeholder="URL (e.g., https://drive.google.com/...)"
                                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                              />
                            </div>
                            <button
                              type="button"
                              onClick={() => handleRemoveDocumentLink(index)}
                              className="px-3 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                              title="Remove link"
                            >
                              <XMarkIcon className="h-5 w-5" />
                            </button>
                          </div>
                        ))}
                        <button
                          type="button"
                          onClick={handleAddDocumentLink}
                          className="flex items-center px-4 py-2 text-sm text-blue-600 hover:bg-blue-50 rounded-lg transition-colors font-medium"
                        >
                          <PlusIcon className="h-4 w-4 mr-1" />
                          Add Document Link
                        </button>
                      </div>
                    </div>

                    {/* Privileges Section */}
                    <div className="md:col-span-2 border-t border-gray-200 pt-6 mt-4">
                      <label className="block text-sm font-bold text-gray-900 mb-3">Privileges</label>
                      <p className="text-xs text-gray-500 mb-4">Configure user permissions and access rights</p>
                      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-4 border border-blue-200 space-y-4">
                        <label className="flex items-center space-x-3 cursor-pointer group">
                          <input
                            type="checkbox"
                            checked={formData.canExportExcel}
                            onChange={(e) => setFormData({ ...formData, canExportExcel: e.target.checked })}
                            className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500 focus:ring-2 transition-all"
                          />
                          <div className="flex-1">
                            <span className="text-sm font-semibold text-gray-900 group-hover:text-blue-700 transition-colors">
                              Show Excel Export
                            </span>
                            <p className="text-xs text-gray-600 mt-1">
                              Allow this user to export data to Excel spreadsheets
                            </p>
                          </div>
                        </label>

                        <label className="flex items-center space-x-3 cursor-pointer group">
                          <input
                            type="checkbox"
                            checked={formData.canComposeEmail}
                            onChange={(e) => setFormData({ ...formData, canComposeEmail: e.target.checked })}
                            className="w-5 h-5 text-green-600 border-gray-300 rounded focus:ring-green-500 focus:ring-2 transition-all"
                          />
                          <div className="flex-1">
                            <span className="text-sm font-semibold text-gray-900 group-hover:text-green-700 transition-colors">
                              Compose Email
                            </span>
                            <p className="text-xs text-gray-600 mt-1">
                              Allow this user to compose and send emails
                            </p>
                          </div>
                        </label>

                        <label className="flex items-center space-x-3 cursor-pointer group">
                          <input
                            type="checkbox"
                            checked={formData.canAccessMobileView}
                            onChange={(e) => setFormData({ ...formData, canAccessMobileView: e.target.checked })}
                            className="w-5 h-5 text-purple-600 border-gray-300 rounded focus:ring-purple-500 focus:ring-2 transition-all"
                          />
                          <div className="flex-1">
                            <span className="text-sm font-semibold text-gray-900 group-hover:text-purple-700 transition-colors">
                              Mobile Access
                            </span>
                            <p className="text-xs text-gray-600 mt-1">
                              Allow this user to access the platform from mobile devices
                            </p>
                          </div>
                        </label>

                        <label className="flex items-center space-x-3 cursor-pointer group">
                          <input
                            type="checkbox"
                            checked={formData.canDeleteContacts}
                            onChange={(e) => setFormData({ ...formData, canDeleteContacts: e.target.checked })}
                            className="w-5 h-5 text-red-600 border-gray-300 rounded focus:ring-red-500 focus:ring-2 transition-all"
                          />
                          <div className="flex-1">
                            <span className="text-sm font-semibold text-gray-900 group-hover:text-red-700 transition-colors">
                              Delete Contacts
                            </span>
                            <p className="text-xs text-gray-600 mt-1">
                              Allow this user to delete contacts from database
                            </p>
                          </div>
                        </label>

                        <label className="flex items-center space-x-3 cursor-pointer group">
                          <input
                            type="checkbox"
                            checked={formData.sendEmailIndividually}
                            onChange={(e) => setFormData({ ...formData, sendEmailIndividually: e.target.checked })}
                            className="w-5 h-5 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500 focus:ring-2 transition-all"
                          />
                          <div className="flex-1">
                            <span className="text-sm font-semibold text-gray-900 group-hover:text-indigo-700 transition-colors">
                              Send Email Individually
                            </span>
                            <p className="text-xs text-gray-600 mt-1">
                              Allow this user to send emails using their own configured sender credentials
                            </p>
                          </div>
                        </label>

                        {/* Individual Email Sending Configuration */}
                        {formData.sendEmailIndividually && (
                          <div className="ml-8 space-y-3 border-l-4 border-indigo-300 pl-4 py-2">
                            <div>
                              <label className="block text-xs font-semibold text-gray-700 mb-1">
                                Sending Email Address *
                              </label>
                              <input
                                type="email"
                                value={formData.sendingEmail}
                                onChange={(e) => setFormData({ ...formData, sendingEmail: e.target.value })}
                                placeholder="user@example.com"
                                required={formData.sendEmailIndividually}
                                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                              />
                              <p className="text-xs text-gray-500 mt-1">
                                The email address to use as sender when this user sends emails
                              </p>
                            </div>
                            <div>
                              <label className="block text-xs font-semibold text-gray-700 mb-1">
                                Email App Password {editingUser && editingUser.hasEmailAppPassword ? '(leave blank to keep current)' : '*'}
                              </label>
                              <input
                                type="password"
                                value={formData.emailAppPassword}
                                onChange={(e) => setFormData({ ...formData, emailAppPassword: e.target.value })}
                                placeholder={editingUser && editingUser.hasEmailAppPassword ? '••••••••' : 'Enter app password'}
                                required={formData.sendEmailIndividually && (!editingUser || !editingUser.hasEmailAppPassword)}
                                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                              />
                              <p className="text-xs text-gray-500 mt-1">
                                {editingUser && editingUser.hasEmailAppPassword 
                                  ? 'Password is set. Enter a new one only if you want to change it.'
                                  : 'App password or SMTP password for the sending email account'}
                              </p>
                            </div>

                            {/* Template Assignment */}
                            <div>
                              <label className="block text-xs font-semibold text-gray-700 mb-2">
                                Assign Templates (Optional)
                              </label>
                              <p className="text-xs text-gray-500 mb-2">
                                Select which templates this user can use. Leave empty to allow all templates.
                              </p>
                              <div className="max-h-40 overflow-y-auto border border-gray-300 rounded-lg p-2 space-y-1 bg-white">
                                {availableTemplates.length === 0 ? (
                                  <p className="text-xs text-gray-400 italic p-2">No templates available</p>
                                ) : (
                                  availableTemplates.map(template => (
                                    <div key={template.id} className="flex items-center justify-between p-2 hover:bg-gray-50 rounded">
                                      <label className="flex items-center cursor-pointer flex-1">
                                        <input
                                          type="checkbox"
                                          checked={formData.assignedTemplateIds.includes(template.id)}
                                          onChange={(e) => {
                                            if (e.target.checked) {
                                              setFormData({
                                                ...formData,
                                                assignedTemplateIds: [...formData.assignedTemplateIds, template.id]
                                              });
                                            } else {
                                              setFormData({
                                                ...formData,
                                                assignedTemplateIds: formData.assignedTemplateIds.filter(id => id !== template.id),
                                                defaultTemplateId: formData.defaultTemplateId === template.id ? null : formData.defaultTemplateId
                                              });
                                            }
                                          }}
                                          className="h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                                        />
                                        <span className="ml-2 text-xs text-gray-700">{template.name}</span>
                                      </label>
                                      {formData.assignedTemplateIds.includes(template.id) && (
                                        <button
                                          type="button"
                                          onClick={() => setFormData({
                                            ...formData,
                                            defaultTemplateId: formData.defaultTemplateId === template.id ? null : template.id
                                          })}
                                          className={`ml-2 px-2 py-0.5 text-xs rounded-full border transition-colors ${
                                            formData.defaultTemplateId === template.id
                                              ? 'bg-indigo-100 border-indigo-400 text-indigo-700 font-semibold'
                                              : 'bg-gray-50 border-gray-300 text-gray-500 hover:bg-indigo-50 hover:border-indigo-300'
                                          }`}
                                          title={formData.defaultTemplateId === template.id ? 'Remove as default' : 'Set as default template'}
                                        >
                                          {formData.defaultTemplateId === template.id ? '★ Default' : '☆ Make Default'}
                                        </button>
                                      )}
                                    </div>
                                  ))
                                )}
                              </div>
                              {formData.assignedTemplateIds.length > 0 && (
                                <p className="text-xs text-indigo-600 mt-1">
                                  {formData.assignedTemplateIds.length} template(s) selected
                                  {formData.defaultTemplateId && ` · Default: ${availableTemplates.find(t => t.id === formData.defaultTemplateId)?.name || ''}`}
                                </p>
                              )}
                            </div>

                            {/* Footer Assignment */}
                            <div>
                              <label className="block text-xs font-semibold text-gray-700 mb-2">
                                Assign Footers (Optional)
                              </label>
                              <p className="text-xs text-gray-500 mb-2">
                                Select which footers this user can use. Leave empty to allow all footers.
                              </p>
                              <div className="max-h-40 overflow-y-auto border border-gray-300 rounded-lg p-2 space-y-1 bg-white">
                                {availableFooters.length === 0 ? (
                                  <p className="text-xs text-gray-400 italic p-2">No footers available</p>
                                ) : (
                                  availableFooters.map(footer => (
                                    <div key={footer.id} className="flex items-center justify-between p-2 hover:bg-gray-50 rounded">
                                      <label className="flex items-center cursor-pointer flex-1">
                                        <input
                                          type="checkbox"
                                          checked={formData.assignedFooterIds.includes(footer.id)}
                                          onChange={(e) => {
                                            if (e.target.checked) {
                                              setFormData({
                                                ...formData,
                                                assignedFooterIds: [...formData.assignedFooterIds, footer.id]
                                              });
                                            } else {
                                              setFormData({
                                                ...formData,
                                                assignedFooterIds: formData.assignedFooterIds.filter(id => id !== footer.id),
                                                defaultFooterId: formData.defaultFooterId === footer.id ? null : formData.defaultFooterId
                                              });
                                            }
                                          }}
                                          className="h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                                        />
                                        <span className="ml-2 text-xs text-gray-700">{footer.name}</span>
                                      </label>
                                      {formData.assignedFooterIds.includes(footer.id) && (
                                        <button
                                          type="button"
                                          onClick={() => setFormData({
                                            ...formData,
                                            defaultFooterId: formData.defaultFooterId === footer.id ? null : footer.id
                                          })}
                                          className={`ml-2 px-2 py-0.5 text-xs rounded-full border transition-colors ${
                                            formData.defaultFooterId === footer.id
                                              ? 'bg-indigo-100 border-indigo-400 text-indigo-700 font-semibold'
                                              : 'bg-gray-50 border-gray-300 text-gray-500 hover:bg-indigo-50 hover:border-indigo-300'
                                          }`}
                                          title={formData.defaultFooterId === footer.id ? 'Remove as default' : 'Set as default footer'}
                                        >
                                          {formData.defaultFooterId === footer.id ? '★ Default' : '☆ Make Default'}
                                        </button>
                                      )}
                                    </div>
                                  ))
                                )}
                              </div>
                              {formData.assignedFooterIds.length > 0 && (
                                <p className="text-xs text-indigo-600 mt-1">
                                  {formData.assignedFooterIds.length} footer(s) selected
                                  {formData.defaultFooterId && ` · Default: ${availableFooters.find(f => f.id === formData.defaultFooterId)?.name || ''}`}
                                </p>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse gap-3">
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full sm:w-auto px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {isSubmitting && <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>}
                    {isSubmitting ? (editingUser ? 'Updating...' : 'Creating...') : (editingUser ? 'Update User' : 'Create User')}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowModal(false);
                      resetForm();
                    }}
                    className="w-full sm:w-auto mt-3 sm:mt-0 px-6 py-2 bg-white text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Users;
